'use client';

import React, { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetPayerTypeSuggestionsInfiniteQuery } from 'src/api/redux';
// Import directly from hook file to avoid circular dependency via hooks/index.ts
import { useGenericComponent } from '../../hooks/useGenericComponent';
import { GenericSelectorModal, GenericNodeComponent, ConnectorType } from '../shared';
import {
  PayerTypeItem,
  payerTypeModalConfig,
  payerTypeNodeConfig,
  transformPayerType,
} from './config';

interface PayerTypeComponentProps {
  componentId: string;
  initialPayerTypes?: PayerTypeItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

// Extract IDs for Redux storage
const getItemIds = (items: PayerTypeItem[]) => items.map((p) => p.name);

const PayerTypeComponent: React.FC<PayerTypeComponentProps> = ({
  componentId,
  initialPayerTypes = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Use generic component hook for state management
  const {
    items: payerTypes,
    connector,
    showModal,
    searchTerm,
    setShowModal,
    setSearchTerm,
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  } = useGenericComponent<PayerTypeItem>({
    componentId,
    type: 'payer_type',
    reduxDataField: 'value',
    getItemIds,
    initialItems: initialPayerTypes,
    initialConnector,
    onUpdate,
    onRemove,
    fromPlaceholder,
  });

  // Debounce search term
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Fetch search results
  const { data, isLoading } = useGetPayerTypeSuggestionsInfiniteQuery({
    q: debouncedSearch,
  });

  // Transform API results to standard format
  const searchResults: PayerTypeItem[] = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results ?? []).map(transformPayerType);
  }, [data]);

  return (
    <>
      {payerTypes.length > 0 ? (
        <GenericNodeComponent
          config={payerTypeNodeConfig}
          items={payerTypes}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-pink-50 border border-pink-200 rounded-full cursor-pointer hover:bg-pink-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-pink-700 text-xs font-medium">
            Select payer types...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={payerTypeModalConfig}
        isOpen={showModal}
        selectedItems={payerTypes}
        connector={connector}
        isEditing={payerTypes.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default PayerTypeComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderPayerTypeComponent = (
  container: HTMLElement,
  props: PayerTypeComponentProps & {
    initialPayerTypes?: PayerTypeItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <PayerTypeComponent {...props} />
    </Provider>
  );
  return root;
};
